/**
 * Dashboard — central launchpad listing every workspace in one grid.
 *
 * Each tile navigates to the corresponding view via the shared navigateTo
 * state machine. Tiles are grouped loosely by category (Trading / Research /
 * Portfolio Ops / Compliance) but all cross-navigate through the same
 * Terminal router, so the Back/Home history works identically.
 */
import type { RailView } from './LeftRail';

interface Props {
  onNavigate: (view: RailView) => void;
  userName?: string;
  onExitToWelcome?: () => void;
}

interface Tile {
  key: RailView;
  glyph: string;
  label: string;
  desc: string;
  group: 'trading' | 'research' | 'portfolio' | 'compliance';
}

const TILES: Tile[] = [
  { key: 'Monitor',     glyph: '\u25A6', label: 'Monitor',       desc: 'Positions + live events feed',                                group: 'portfolio' },
  { key: 'Trade',       glyph: '\u25D1', label: 'Trade',         desc: 'Active trader + NGX order ticket',                            group: 'trading' },
  { key: 'Analyze',     glyph: '\u25CE', label: 'Analyze',       desc: 'Multi-chart charts-grid workspace',                           group: 'research' },
  { key: 'Scan',        glyph: '\u2315', label: 'Scan',          desc: 'Scanner with 59 fundamental + technical filters',             group: 'research' },
  { key: 'Charts',      glyph: '\u2B1A', label: 'Charts',        desc: 'Candlestick charts with drawings + studies',                  group: 'research' },
  { key: 'Ratings',     glyph: '\u2605', label: 'Ratings',       desc: 'Credit ratings from Agusto, GCR, DataPro, Fitch',             group: 'research' },
  { key: 'Tools',       glyph: '\u2699', label: 'Tools',         desc: 'Quote details + options analytics',                           group: 'research' },
  { key: 'Bond',        glyph: '\u25A4', label: 'Bond',          desc: 'FGN / Sukuk / T-Bill / Municipal / Corporate trader',         group: 'trading' },
  { key: 'ETF',         glyph: '\u25A3', label: 'ETF',           desc: '11 NGX ETFs across Broad, Sector, Commodity, Sharia',         group: 'trading' },
  { key: 'Orders',      glyph: '\u2263', label: 'Orders',        desc: 'Cross-broker blotter with cancel/modify',                     group: 'portfolio' },
  { key: 'Brokers',     glyph: '\u2735', label: 'Brokers',       desc: 'Link broker accounts via OAuth PKCE',                         group: 'portfolio' },
  { key: 'Recon',       glyph: '\u21CC', label: 'Reconciliation',desc: 'Diff local cache vs. broker book-of-record',                  group: 'compliance' },
  { key: 'Funding',     glyph: '\u20A6', label: 'Funding',       desc: 'Deposits + withdrawals via NIBSS / Paystack / DD',            group: 'portfolio' },
  { key: 'CorpActions', glyph: '\u272A', label: 'Corp Actions',  desc: 'Dividends, splits, rights issues, AGMs',                      group: 'compliance' },
  { key: 'BestEx',      glyph: '\u2605', label: 'Best-Execution',desc: 'SEC 606/607 execution-quality report',                        group: 'compliance' },
];

const GROUPS: Array<{ id: Tile['group']; label: string; hint: string }> = [
  { id: 'trading',    label: 'Trading',        hint: 'Place orders across asset classes.' },
  { id: 'research',   label: 'Research',       hint: 'Analyse instruments before trading.' },
  { id: 'portfolio',  label: 'Portfolio Ops',  hint: 'Track positions, orders, funding.' },
  { id: 'compliance', label: 'Compliance',     hint: 'Reconciliation, best-ex, corporate actions.' },
];

export function DashboardView({ onNavigate, userName, onExitToWelcome }: Props) {
  const firstName = (() => {
    if (!userName) return undefined;
    const local = userName.split('@')[0] ?? userName;
    const raw = local.split(/[.\-_]/)[0] ?? local;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  })();
  return (
    <div className="dash">
      {onExitToWelcome && (
        <div style={{ padding: '8px 16px', background: '#1a1f2e', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={onExitToWelcome}
            style={{
              background: 'none',
              border: 'none',
              color: '#66b3ff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              padding: '4px 8px',
            }}
          >
            ← Back to Welcome
          </button>
        </div>
      )}
      <div className="dash-head">
        <div className="dash-head-row">
          <div>
            <h2>Dashboard</h2>
            <div className="dash-sub">Launch any workspace in the trader. Everything here is also reachable from the left rail.</div>
          </div>
          {firstName && (
            <div className="dash-welcome">
              <span className="dash-welcome-txt">Welcome</span>
              <span className="dash-welcome-name">{firstName}!</span>
            </div>
          )}
        </div>
      </div>

      <div className="dash-sections">
      {GROUPS.map((g) => {
        const items = TILES.filter((t) => t.group === g.id);
        return (
          <section key={g.id} className="dash-section">
            <div className="dash-section-h">
              <h3>{g.label}</h3>
              <span className="dash-section-hint">{g.hint}</span>
            </div>
            <div className="dash-grid">
              {items.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={'dash-tile group-' + t.group}
                  onClick={() => onNavigate(t.key)}
                >
                  <span className="dash-glyph" aria-hidden="true">{t.glyph}</span>
                  <span className="dash-label">{t.label}</span>
                  <span className="dash-desc">{t.desc}</span>
                </button>
              ))}
            </div>
          </section>
        );
      })}
      </div>
    </div>
  );
}

